/**
 * The `expo-sqlite` sync API, over SQLite compiled to WebAssembly.
 *
 * The phone's data layer (`apps/mobile/src/lib/entry-repo.ts` and the Drizzle
 * driver `drizzle-orm/expo-sqlite/driver`) talks to a `SQLiteDatabase` from
 * `expo-sqlite`. This class is that object for the browser, and only the part
 * of it those two callers use:
 *
 *   - `prepareSync` → a statement with `executeSync`, `executeForRawResultSync`
 *     and `finalizeSync`
 *   - `execSync`, `getAllSync`, `getFirstSync`, `runSync`
 *
 * A statement runs to completion inside `executeSync`: every row is read then,
 * and the result object only hands them out. Nothing is ever left half-stepped,
 * so one prepared statement can serve the next caller at once.
 *
 * **Why statements are cached.** Drizzle prepares a statement for every query
 * and never finalizes it (the phone's `db.ts` says so at length). On the phone
 * that costs native memory; here it costs WebAssembly heap, which does not
 * shrink. So `prepareSync` hands out one statement per SQL text, held in a
 * small LRU, and `finalizeSync` on a cached statement is a no-op: the cache
 * owns its lifetime.
 */
import type { BindingSpec, Database, PreparedStatement, Sqlite3Static, SqlValue } from '@sqlite.org/sqlite-wasm';

/** What `expo-sqlite` accepts as one bound value. */
export type BindValue = string | number | bigint | boolean | null | undefined | Uint8Array;

/** What `expo-sqlite` accepts as the parameters of one statement: positional or named. */
export type BindParams = readonly BindValue[] | Readonly<Record<string, BindValue>>;

/** The receipt of a statement that ran. The row readers mirror `SQLiteExecuteSyncResult`. */
export type ExecuteResult<T> = {
  readonly changes: number;
  readonly lastInsertRowId: number;
  getAllSync(): T[];
  getFirstSync(): T | null;
  resetSync(): void;
};

export type RawExecuteResult = {
  readonly changes: number;
  readonly lastInsertRowId: number;
  getAllSync(): SqlValue[][];
  getFirstSync(): SqlValue[] | null;
  resetSync(): void;
};

/** How many prepared statements stay alive at once. */
const STATEMENT_CACHE_SIZE = 256;

const toBindValue = (value: BindValue): SqlValue => {
  if (value === undefined) return null;
  if (typeof value === 'boolean') return value ? 1 : 0;
  return value;
};

const toBinding = (params: BindParams | undefined): BindingSpec | null => {
  if (params === undefined) return null;
  if (Array.isArray(params)) {
    if (params.length === 0) return null;
    return (params as readonly BindValue[]).map(toBindValue);
  }
  const named: Record<string, SqlValue> = {};
  let any = false;
  for (const [key, value] of Object.entries(params as Readonly<Record<string, BindValue>>)) {
    named[key] = toBindValue(value);
    any = true;
  }
  return any ? named : null;
};

type Rows = { readonly columns: readonly string[]; readonly values: SqlValue[][] };

const toObjects = <T>(rows: Rows): T[] =>
  rows.values.map((row) => {
    const object: Record<string, SqlValue> = {};
    rows.columns.forEach((column, index) => {
      object[column] = row[index] ?? null;
    });
    return object as T;
  });

/**
 * One SQL text, prepared lazily and re-prepared if the cache evicted it while
 * a caller still held this handle.
 */
export class WebSqliteStatement {
  private stmt: PreparedStatement | null = null;
  private finalized = false;

  constructor(
    private readonly owner: WebSqliteDatabase,
    readonly sql: string,
    private readonly cached: boolean,
  ) {}

  private prepared(): PreparedStatement {
    if (this.finalized) throw new Error('This statement was finalized.');
    if (this.stmt === null) this.stmt = this.owner.raw.prepare(this.sql);
    return this.stmt;
  }

  private run(params: BindParams | undefined): Rows & { changes: number; lastInsertRowId: number } {
    const stmt = this.prepared();
    stmt.reset(true);
    const binding = toBinding(params);
    if (binding !== null && stmt.parameterCount > 0) stmt.bind(binding);
    const columns = stmt.columnCount > 0 ? stmt.getColumnNames([]) : [];
    const values: SqlValue[][] = [];
    try {
      while (stmt.step()) values.push(stmt.get([]));
    } finally {
      stmt.reset(true);
    }
    const readOnly = this.owner.isReadOnly(stmt);
    if (!readOnly) this.owner.noteWrite();
    return {
      columns,
      values,
      changes: readOnly ? 0 : this.owner.raw.changes(),
      lastInsertRowId: this.owner.lastInsertRowId(),
    };
  }

  executeSync<T>(params?: BindParams): ExecuteResult<T> {
    const rows = this.run(params);
    const objects = toObjects<T>(rows);
    return {
      changes: rows.changes,
      lastInsertRowId: rows.lastInsertRowId,
      getAllSync: () => objects,
      getFirstSync: () => objects[0] ?? null,
      resetSync: () => undefined,
    };
  }

  executeForRawResultSync(params?: BindParams): RawExecuteResult {
    const rows = this.run(params);
    return {
      changes: rows.changes,
      lastInsertRowId: rows.lastInsertRowId,
      getAllSync: () => rows.values,
      getFirstSync: () => rows.values[0] ?? null,
      resetSync: () => undefined,
    };
  }

  /** A cached statement belongs to the cache, so finalizing it here does nothing. */
  finalizeSync(): void {
    if (this.cached) return;
    this.release();
    this.finalized = true;
  }

  /** @internal The cache calls this when it evicts the statement. */
  release(): void {
    this.stmt?.finalize();
    this.stmt = null;
  }
}

export class WebSqliteDatabase {
  private readonly statements = new Map<string, WebSqliteStatement>();
  private writes = 0;
  private readonly listeners = new Set<() => void>();

  constructor(
    readonly sqlite3: Sqlite3Static,
    readonly raw: Database,
  ) {}

  /** @internal */
  isReadOnly(stmt: PreparedStatement): boolean {
    return this.sqlite3.capi.sqlite3_stmt_readonly(stmt) !== 0;
  }

  /** @internal */
  lastInsertRowId(): number {
    return Number(this.sqlite3.capi.sqlite3_last_insert_rowid(this.raw));
  }

  /** @internal Every write lands here, so persistence knows the file changed. */
  noteWrite(): void {
    this.writes += 1;
    for (const listener of this.listeners) listener();
  }

  /** How many writes this connection has made. Persistence compares it to decide whether to save. */
  get writeCount(): number {
    return this.writes;
  }

  /** Called after every write. Returns the unsubscribe. */
  onWrite(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  prepareSync(sql: string): WebSqliteStatement {
    const hit = this.statements.get(sql);
    if (hit !== undefined) {
      // Re-insert to mark it most recently used.
      this.statements.delete(sql);
      this.statements.set(sql, hit);
      return hit;
    }
    const statement = new WebSqliteStatement(this, sql, true);
    this.statements.set(sql, statement);
    if (this.statements.size > STATEMENT_CACHE_SIZE) {
      const oldest = this.statements.keys().next().value;
      if (oldest !== undefined) {
        this.statements.get(oldest)?.release();
        this.statements.delete(oldest);
      }
    }
    return statement;
  }

  /** Runs one or more statements with no parameters and no result, as `expo-sqlite` does. */
  execSync(sql: string): void {
    this.raw.exec(sql);
    this.noteWrite();
  }

  getAllSync<T>(sql: string, params?: BindParams): T[] {
    return this.prepareSync(sql).executeSync<T>(params).getAllSync();
  }

  getFirstSync<T>(sql: string, params?: BindParams): T | null {
    return this.prepareSync(sql).executeSync<T>(params).getFirstSync();
  }

  runSync(sql: string, params?: BindParams): { changes: number; lastInsertRowId: number } {
    const result = this.prepareSync(sql).executeSync<unknown>(params);
    return { changes: result.changes, lastInsertRowId: result.lastInsertRowId };
  }

  /** The whole database as bytes, for the snapshot. */
  exportBytes(): Uint8Array {
    return this.sqlite3.capi.sqlite3_js_db_export(this.raw);
  }

  /** Every cached statement is released first: SQLite refuses to close a connection with live ones. */
  close(): void {
    for (const statement of this.statements.values()) statement.release();
    this.statements.clear();
    this.raw.close();
  }
}

/**
 * Opens an in-memory database, and loads `bytes` into it when a snapshot
 * exists. A snapshot that does not open is dropped rather than trusted: the
 * mirror is a cache the next pull refills.
 */
export const openWebDatabase = (sqlite3: Sqlite3Static, bytes: Uint8Array | null): WebSqliteDatabase => {
  const raw = new sqlite3.oo1.DB(':memory:', 'c');
  if (bytes !== null && bytes.byteLength > 0) {
    const pointer = sqlite3.wasm.allocFromTypedArray(bytes);
    const { capi } = sqlite3;
    const code = capi.sqlite3_deserialize(
      raw,
      'main',
      pointer,
      bytes.byteLength,
      bytes.byteLength,
      capi.SQLITE_DESERIALIZE_FREEONCLOSE | capi.SQLITE_DESERIALIZE_RESIZEABLE,
    );
    if (code !== capi.SQLITE_OK) {
      raw.close();
      return new WebSqliteDatabase(sqlite3, new sqlite3.oo1.DB(':memory:', 'c'));
    }
    try {
      raw.exec('PRAGMA quick_check');
    } catch {
      raw.close();
      return new WebSqliteDatabase(sqlite3, new sqlite3.oo1.DB(':memory:', 'c'));
    }
  }
  return new WebSqliteDatabase(sqlite3, raw);
};
